import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import Icon from 'components/Icon'
import classNames from 'classnames'

const ProcessStep = ({
  icon,
  iconStyle = 'far',
  title,
  isActive,
  isCompleted,
}) => {
  return (
    <div className="flex md:flex-col items-center text-left md:text-center shrink-0 md:w-40">
      <div className="flex items-center justify-center mb-0 md:mb-2">
        <div
          className={classNames(
            'flex items-center justify-center rounded-full transition-all duration-300',
            'w-9 h-9 md:w-12 md:h-12',
            {
              'bg-primary text-white shadow-md md:scale-110': isActive,
              'bg-positive text-white': isCompleted && !isActive,
              'bg-gray-200 text-gray-500': !isActive && !isCompleted,
            }
          )}
        >
          {isCompleted && !isActive ? (
            <Icon icon="check" className="text-sm md:text-xl" />
          ) : (
            <Icon
              icon={[iconStyle, icon]}
              className="text-sm md:text-xl"
            />
          )}
        </div>
      </div>

      <span
        className={classNames(
          'ml-3 md:ml-0 text-sm md:text-sm font-medium leading-tight transition-colors duration-300',
          {
            'text-primary font-semibold': isActive,
            'text-positive': isCompleted && !isActive,
            'text-gray-600': !isActive && !isCompleted,
          }
        )}
      >
        {title}
      </span>
    </div>
  )
}

const getCurrentStep = (project) => {
  if (!project) {
    return 0
  }

  const hasFilters =
    project.categories?.length > 0 ||
    project.genders?.length > 0 ||
    project.languages?.length > 0 ||
    project.tags?.length > 0 ||
    project.mediums?.length > 0 ||
    project.collections?.length > 0 ||
    project.query

  const hasSelectedVoices =
    project.voices && Object.keys(project.voices).length > 0

  const hasRequestedQuote =
    project.details?.name ||
    project.details?.email ||
    project.leadId ||
    project.dealId

  if (hasRequestedQuote) return 3
  if (hasSelectedVoices) return 2
  if (hasFilters) return 1
  return 0
}

export const InitialProcess = ({ language, project }) => {
  const { formatMessage } = useIntl()
  const currentStep = getCurrentStep(project)

  const steps = [
    {
      icon: 'sliders-h',
      title: formatMessage(messages.step1Title),
      iconStyle: 'far',
    },
    {
      icon: 'heart',
      title: formatMessage(messages.step2Title),
      iconStyle: 'far',
    },
    {
      icon: 'download',
      title: formatMessage(messages.step3Title),
      iconStyle: 'far',
    },
  ]

  return (
    <section className="bg-white border-b border-gray-200">
      <div className="container mx-auto px-4 md:px-8 py-8 md:py-12">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          {language ? (
            <FormattedMessage
              {...messages.headline}
              values={{
                language: <span className="capitalize">{language}</span>,
              }}
            />
          ) : (
            <FormattedMessage {...messages.headlineDefault} />
          )}
        </h1>

        <p className="text-base md:text-lg text-gray-600 mb-8 max-w-3xl">
          <FormattedMessage {...messages.description} />
        </p>

        <div className="mt-8">
          <h2 className="text-sm font-medium text-gray-500 mb-6">
            <FormattedMessage {...messages.processTitle} />
          </h2>

          <div className="flex flex-col md:flex-row w-full">
            {steps.map((step, index) => {
              const isActive = currentStep === index
              const isCompleted = currentStep > index

              return (
                <React.Fragment key={index}>
                  <ProcessStep
                    icon={step.icon}
                    iconStyle={step.iconStyle}
                    title={step.title}
                    isActive={isActive}
                    isCompleted={isCompleted}
                  />

                  {index < steps.length - 1 && (
                    <>
                      <div className="md:hidden ml-4 h-6 w-px bg-gray-300 my-2" />
                      <div className="hidden md:flex flex-1 items-center px-4">
                        <div
                          className={classNames(
                            'h-0.5 w-full transition-colors duration-300',
                            {
                              'bg-positive': isCompleted,
                              'bg-gray-300': !isCompleted,
                            }
                          )}
                        />
                      </div>
                    </>
                  )}
                </React.Fragment>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

export default InitialProcess
